import { Button, Close, IconButton } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import complianceAppIcon from 'images/compliance-app-icon.svg';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { usePostHog } from 'hooks/usePostHog';

import { CUE_COMPLIANCE_UPSELL_CTA } from './analytics-events';
import { ComplianceUpsellModal } from './ComplianceUpsellModal';
import messages from './messages';
import { useStyles } from './styles';

export interface ComplianceUpsellStageCardProps {
  openingTitle: string;
  stageTitle: string;
  /**
   * Whether the Cue CTA can be offered. Accepting the modal's trial CTA is only
   * meaningful when the Cue panel is actually mounted for this account, so when
   * this is false the card renders as awareness-only rather than showing a
   * button that would open a modal leading nowhere.
   */
  showDiscoverCta: boolean;
}

// Promo card for the Compliance upsell, matching the Figma handoff
// (node 12381:195402). Rendered directly below an eligible data-collection stage
// in the workflow editor's stage list; StageColumn owns the gating
// (`show_compliance_upsell` on the stage plus the `wx-compliance-upsell` flag).
//
// "Discover" opens ComplianceUpsellModal, which carries the pitch. The modal's
// "Start free trial" is the card's old Discover action — it opens the Cue panel
// with a prompt naming this workflow and stage. Everything after that —
// matching the file questions to compliance document types, asking for consent,
// and transforming the workflow — belongs to the Compliance Upsell Agent
// (CMP-1810), not to this component.
//
// Dismissal is per-render only and intentionally not persisted; if it should
// survive a reload that needs a real preference store. Closing the modal
// leaves the card in place and is re-openable.
export const ComplianceUpsellStageCard: React.FC<
  ComplianceUpsellStageCardProps
> = ({ openingTitle, stageTitle, showDiscoverCta }) => {
  const classes = useStyles();
  const intl = useIntl();
  const posthog = usePostHog();
  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // The button impression and the modal open are separate facts. CueCtaButton
  // fires its view on mount, so it moved into the modal; the card reports this
  // one itself and would otherwise count a modal open.
  //
  // An awareness-only card has no button, so it reports nothing: an impression
  // there would enter the view-to-click rate with no click available to it.
  useEffect(() => {
    if (!showDiscoverCta) {
      return;
    }

    posthog.capture(CUE_COMPLIANCE_UPSELL_CTA.viewEvent, {
      cta_id: CUE_COMPLIANCE_UPSELL_CTA.ctaId,
    });
  }, [posthog, showDiscoverCta]);

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    posthog.capture(CUE_COMPLIANCE_UPSELL_CTA.closeEvent, {
      cta_id: CUE_COMPLIANCE_UPSELL_CTA.ctaId,
    });
    setDismissed(true);
  };

  const handleDiscover = () => {
    posthog.capture(CUE_COMPLIANCE_UPSELL_CTA.clickEvent, {
      cta_id: CUE_COMPLIANCE_UPSELL_CTA.ctaId,
    });
    setModalOpen(true);
  };

  return (
    <div className={classes.card}>
      <img alt="" className={classes.icon} src={complianceAppIcon} />

      <div className={classes.content}>
        <div className={classes.headerRow}>
          <div>
            <Typography className={classes.title} variant="body1">
              {intl.formatMessage(messages.title)}
            </Typography>
            <Typography className={classes.subtitle} variant="body2">
              {intl.formatMessage(messages.subtitle)}
            </Typography>
          </div>

          <IconButton
            aria-label={intl.formatMessage(messages.dismissAriaLabel)}
            className={classes.dismissButton}
            onClick={handleDismiss}
            size="small"
          >
            <Close />
          </IconButton>
        </div>

        {showDiscoverCta && (
          <div className={classes.discoverButtonWrapper}>
            <Button onClick={handleDiscover} size="small" type="secondary">
              {intl.formatMessage(messages.discover)}
            </Button>
          </div>
        )}
      </div>

      {/* Mounted only while open, so the modal's view event counts opens and
          the closed dialog leaves no DOM (MUI keeps a closing Dialog mounted
          through its exit transition). */}
      {showDiscoverCta && modalOpen && (
        <ComplianceUpsellModal
          onClose={() => setModalOpen(false)}
          open={modalOpen}
          openingTitle={openingTitle}
          stageTitle={stageTitle}
        />
      )}
    </div>
  );
};
